import React, { useCallback, useEffect } from "react";
import { Box, AHText, TouchableBox } from "arkh";
import * as Haptics from "expo-haptics";
import { FlatList, Platform } from "react-native";
import { graphql, usePaginationFragment } from "react-relay";
import { KPISelectorList_Routine$key } from "./__generated__/KPISelectorList_Routine.graphql";

export function KPISelectorList(props: {
  routine: KPISelectorList_Routine$key;
  formatDate: (date: string) => [string, string];
  currentSequenceNumber: number;
  onSelectOccasion: (sequenceNumber: number) => void;
}) {
  const { data, hasNext, loadNext } = usePaginationFragment(
    graphql`
      fragment KPISelectorList_Routine on Routine
      @refetchable(queryName: "KPIBottomSheetOccasionSelectorRefetchQuery") {
        id
        occasions(first: $first, after: $after)
          @connection(key: "KPIBottomSheetPaginatedListComponent_occasions") {
          edges {
            node {
              id
              startAt
              sequenceNumber
            }
          }
        }
      }
    `,
    props.routine
  );

  useEffect(() => {
    if (Platform.OS === "web") {
      document.onkeydown = function checkKey(e: any) {
        e = e || window.event;

        const currentIndex = data.occasions.edges.findIndex((edge) => {
          return edge.node.sequenceNumber === props.currentSequenceNumber;
        });

        if (e.keyCode === 39) {
          if (currentIndex === 0) {
            loadNext(5);
          }
          if (currentIndex > 0) {
            const prevIndex = currentIndex - 1;
            if (prevIndex >= 0) {
              props.onSelectOccasion(
                data.occasions.edges[prevIndex].node.sequenceNumber
              );
            }
          }
        } else if (e.keyCode === 37) {
          if (currentIndex >= 0) {
            const nextIndex = currentIndex + 1;
            if (nextIndex < data.occasions.edges.length) {
              props.onSelectOccasion(
                data.occasions.edges[nextIndex].node.sequenceNumber
              );
            }
          }
        }
      };
      return () => {
        document.onkeydown = null;
      };
    }
  }, [data, loadNext, props]);

  const renderItem = useCallback(
    ({ item }: { item: (typeof data.occasions.edges)[number] }) => {
      const isCurrent =
        item.node.sequenceNumber === props.currentSequenceNumber;

      const [titleDate, infoDate] = props.formatDate(item.node.startAt);
      return (
        <TouchableBox
          spaceHorizontal="small"
          onPressIn={() => {
            if (Platform.OS === "ios") {
              Haptics.impactAsync();
            }
          }}
          onPress={() => {
            props.onSelectOccasion(item.node.sequenceNumber);
          }}
        >
          <Box
            style={{
              minWidth: 40,
              height: 40,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 50,
            }}
            color={isCurrent ? "darkerText" : "surface"}
          >
            <AHText
              color={isCurrent ? "lightText" : "darkerText"}
              textAlign="center"
              type="subtitle"
              spaceHorizontal="medium"
            >
              {titleDate}
            </AHText>
            <AHText
              color={isCurrent ? "lightText" : "darkerText"}
              textAlign="center"
              type="detail"
              spaceHorizontal="medium"
            >
              {infoDate}
            </AHText>
          </Box>
        </TouchableBox>
      );
    },
    [data, props]
  );

  return (
    <FlatList
      keyboardShouldPersistTaps="handled"
      renderItem={renderItem}
      horizontal
      inverted
      showsHorizontalScrollIndicator={false}
      data={data.occasions.edges}
      onEndReachedThreshold={0.5}
      onEndReached={() => {
        if (hasNext) {
          loadNext(5);
        }
      }}
    />
  );
}
